import { useState, useEffect, useRef, useMemo } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { RESTAURANT_ID } from '../constants';
import { logAudit } from '../auditLog';
import { printNewOrderReceipt, printCancelReceipt } from '../utils/printer';
import { scheduledLabel } from '../utils/schedule';
import { Empty, Icon, Loading, Modal, ORDER_STATUS, StatusBadge, Switch, tl, useToast } from '../ui';
import './orders.css';

const VALID_TRANSITIONS = {
  pending: ['preparing', 'cancelled'],
  preparing: ['ready', 'on_the_way', 'cancelled'],
  ready: ['delivered', 'cancelled'],
  on_the_way: ['delivered', 'cancelled'],
  delivered: [],
  cancelled: [],
};

// Bekleyen siparişlerin ilk görülme zamanı (otomatik kabul geri sayımı için) localStorage'da tutulur.
const LS_KEY = `pendingSeenAt_${RESTAURANT_ID}`;
const loadPendingSeenAt = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; } };
const savePendingSeenAt = (obj) => { try { localStorage.setItem(LS_KEY, JSON.stringify(obj)); } catch { /* yok say */ } };

const shortId = (id) => `#${id.substring(0, 6).toUpperCase()}`;
const toDate = (o) => o.createdAt?.toDate?.() || null;
const minutesSince = (o, now) => { const d = toDate(o); return d ? Math.max(0, Math.floor((now - d) / 60000)) : 0; };
const fmtDateTime = (d) => (d ? d.toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const NOTIFY_TEMPLATES = [
  { title: 'Gecikme bildirimi', body: 'Siparişiniz biraz gecikecek, anlayışınız için teşekkür ederiz.' },
  { title: 'Kurye yolda', body: 'Siparişiniz kurye tarafından teslim alındı, yola çıktı!' },
  { title: 'Ürün tükendi', body: 'Siparişinizdeki bir ürün tükendi, sizi arayacağız.' },
  { title: 'Adres onayı', body: 'Teslimat adresinizi teyit etmek için sizi arayacağız.' },
];

function Orders({ autoPrint, setAutoPrint, autoAccept, setAutoAccept }) {
  const toast = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('active');
  const [selectedId, setSelectedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [now, setNow] = useState(new Date());
  const [userOrderCounts, setUserOrderCounts] = useState({});
  const [cancelTarget, setCancelTarget] = useState(null);
  const [notifyTarget, setNotifyTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const pendingSeenAt = useRef(loadPendingSeenAt());
  const isInitialLoad = useRef(true);

  // ── Veri: aktif siparişler + son 24 saatin biten siparişleri ──────────
  useEffect(() => {
    const activeQ = query(collection(db, 'orders'), where('restaurantId', '==', RESTAURANT_ID),
      where('status', 'in', ['pending', 'preparing', 'on_the_way', 'ready']));
    const since24h = Timestamp.fromDate(new Date(Date.now() - 24 * 60 * 60 * 1000));
    const pastQ = query(collection(db, 'orders'), where('restaurantId', '==', RESTAURANT_ID),
      where('status', 'in', ['delivered', 'cancelled']), where('createdAt', '>=', since24h));

    let activeOrders = [];
    let pastOrders = [];

    const merge = (source, data) => {
      if (source === 'active') activeOrders = data;
      if (source === 'past') pastOrders = data;
      const merged = [...activeOrders, ...pastOrders].sort((a, b) => (toDate(b) || 0) - (toDate(a) || 0));

      const counts = {};
      merged.forEach((o) => { if (o.status !== 'cancelled') counts[o.userId] = (counts[o.userId] || 0) + 1; });
      setUserOrderCounts(counts);

      const seen = loadPendingSeenAt();
      if (isInitialLoad.current) {
        if (source === 'past') {
          merged.forEach((o) => {
            if (o.status === 'pending' && !seen[o.id]) seen[o.id] = toDate(o)?.getTime() || Date.now();
          });
          savePendingSeenAt(seen);
          pendingSeenAt.current = seen;
          isInitialLoad.current = false;
          setLoading(false);
        }
        setOrders(merged);
        return;
      }
      merged.forEach((o) => {
        if (o.status === 'pending' && !seen[o.id]) seen[o.id] = Date.now();
        if (o.status !== 'pending' && seen[o.id]) delete seen[o.id];
      });
      savePendingSeenAt(seen);
      pendingSeenAt.current = seen;
      setOrders(merged);
    };

    const unsubActive = onSnapshot(activeQ, (snap) => merge('active', snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
    const unsubPast = onSnapshot(pastQ, (snap) => merge('past', snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
    return () => { unsubActive(); unsubPast(); };
  }, []);

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 5000); return () => clearInterval(t); }, []);

  const selected = orders.find((o) => o.id === selectedId) || null;

  // ── Durum değişiklikleri ───────────────────────────────────────────
  const updateStatus = async (order, status) => {
    const allowed = VALID_TRANSITIONS[order.status] || [];
    if (!allowed.includes(status)) { toast(`Bu adım şu an yapılamaz (${ORDER_STATUS[order.status]?.label} → ${ORDER_STATUS[status]?.label}).`, 'error'); return; }
    // Sunucu fiyatı doğrulamadan kabul edilmez (iptal hariç).
    if (order.serverVerified !== true && status !== 'cancelled') { toast('Sipariş henüz sunucuda doğrulanmadı, birkaç saniye bekleyin.', 'error'); return; }
    setBusyId(order.id);
    try {
      const payload = { status };
      if (status === 'ready') {
        payload.pendingNotification = { title: 'Siparişiniz Hazır! 🎉', body: 'Gelip alabilirsiniz, sizi bekliyoruz.', sentAt: new Date() };
      }
      await updateDoc(doc(db, 'orders', order.id), payload);
      logAudit('order_status_changed', order.id, { from: order.status, to: status });
    } catch (e) {
      console.error('Durum güncellenemedi:', e);
      toast('Durum güncellenemedi, lütfen tekrar deneyin.', 'error');
    }
    setBusyId(null);
  };

  const nextAction = (o) => {
    if (o.status === 'pending') return { status: 'preparing', label: 'Kabul et', icon: 'check' };
    if (o.status === 'preparing') return o.orderType === 'pickup'
      ? { status: 'ready', label: 'Hazır (müşteriye bildir)', icon: 'bag' }
      : { status: 'on_the_way', label: 'Kuryeye verildi', icon: 'bike' };
    if (o.status === 'ready' || o.status === 'on_the_way') return { status: 'delivered', label: 'Teslim edildi', icon: 'check' };
    return null;
  };

  const countdown = (o) => {
    if (o.status !== 'pending' || !autoAccept) return null;
    const seenAt = pendingSeenAt.current[o.id];
    return seenAt ? Math.max(0, 60 - Math.floor((now - seenAt) / 1000)) : null;
  };

  const persist = (key, value, setter) => { setter(value); localStorage.setItem(key, String(value)); };


  // ── Listeler ───────────────────────────────────────────────────────
  const matches = (o) => {
    const q = searchQuery.trim().toLocaleLowerCase('tr-TR');
    if (!q) return true;
    return [o.id, o.userName, o.userEmail, o.userPhone, o.addressPhone]
      .some((f) => String(f || '').toLocaleLowerCase('tr-TR').includes(q));
  };
  const ACTIVE = ['pending', 'preparing', 'ready', 'on_the_way'];
  const STATUS_RANK = { pending: 0, preparing: 1, ready: 2, on_the_way: 2, delivered: 3, cancelled: 4 };
  const todayStr = new Date().toDateString();

  const lists = useMemo(() => {
    const byNewest = (a, b) => (toDate(b) || 0) - (toDate(a) || 0);
    const active = orders.filter((o) => ACTIVE.includes(o.status)).filter(matches)
      .sort((a, b) => (STATUS_RANK[a.status] - STATUS_RANK[b.status]) || ((toDate(a) || 0) - (toDate(b) || 0)));
    const today = orders.filter((o) => toDate(o)?.toDateString() === todayStr).filter(matches).sort(byNewest);
    const past = orders.filter((o) => ['delivered', 'cancelled'].includes(o.status)).filter(matches).sort(byNewest);
    return { active, today, past };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, searchQuery, todayStr]);

  const todays = orders.filter((o) => toDate(o)?.toDateString() === todayStr);
  const stats = {
    active: orders.filter((o) => ACTIVE.includes(o.status)).length,
    pending: orders.filter((o) => o.status === 'pending').length,
    delivered: todays.filter((o) => o.status === 'delivered').length,
    revenue: todays.filter((o) => o.status === 'delivered').reduce((s, o) => s + (Number(o.totalPrice) || 0), 0),
    cancelled: todays.filter((o) => o.status === 'cancelled').length,
  };
  const currentList = view === 'today' ? lists.today : view === 'past' ? lists.past : lists.active;

  // Seçili sipariş yoksa en eski bekleyen siparişi otomatik seç.
  useEffect(() => {
    if (selectedId && orders.some((o) => o.id === selectedId)) return;
    const firstPending = lists.active.find((o) => o.status === 'pending');
    setSelectedId(firstPending ? firstPending.id : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  if (loading) return <div className="k-page"><Loading text="Siparişler yükleniyor…" /></div>;

  return (
    <div className="k-page ko-page">
      <div className="ko-toolbar">
        <div className="k-seg">
          <button type="button" className={view === 'active' ? 'on' : ''} onClick={() => setView('active')}>Aktif<span className="k-seg-count">{lists.active.length}</span></button>
          <button type="button" className={view === 'today' ? 'on' : ''} onClick={() => setView('today')}>Bugün<span className="k-seg-count">{lists.today.length}</span></button>
          <button type="button" className={view === 'past' ? 'on' : ''} onClick={() => setView('past')}>Son 24 saat (biten)<span className="k-seg-count">{lists.past.length}</span></button>
        </div>
        <div className="k-search ko-search">
          <Icon name="search" size={15} />
          <input className="k-input" placeholder="Sipariş no, müşteri, telefon" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
        <div className="ko-settings">
          <Switch checked={autoAccept} onChange={(v) => persist('autoAccept', v, setAutoAccept)} label="Otomatik kabul (1 dk)" />
          <Switch checked={autoPrint} onChange={(v) => persist('autoPrint', v, setAutoPrint)} label="Otomatik fiş" />
        </div>
      </div>

      <div className="ko-summary">
        <span>Bekleyen <b className="tnum">{stats.pending}</b></span>
        <span>Aktif <b className="tnum">{stats.active}</b></span>
        <span>Bugün teslim <b className="tnum">{stats.delivered}</b></span>
        <span>Bugünkü ciro <b className="tnum">{tl(stats.revenue)}</b></span>
        <span>Bugün iptal <b className="tnum">{stats.cancelled}</b></span>
      </div>

      <div className="ko-split">
        <div className="k-card ko-list">
          {currentList.length === 0 ? (
            <Empty title={view === 'active' ? 'Aktif sipariş yok' : 'Sipariş yok'}>{view === 'active' ? 'Yeni sipariş geldiğinde burada görünür ve sesli uyarı verilir.' : ''}</Empty>
          ) : (
            <table className="k-table ko-table">
              <thead>
                <tr><th>Saat</th><th>No</th><th>Müşteri</th><th>Tür</th><th>Ödeme</th><th style={{ textAlign: 'right' }}>Tutar</th><th>Durum</th><th style={{ textAlign: 'right' }}>Süre</th></tr>
              </thead>
              <tbody>
                {currentList.map((o) => {
                  const mins = minutesSince(o, now);
                  const late = (o.status === 'pending' && mins >= 5) || (o.status === 'preparing' && mins >= 25);
                  return (
                    <tr key={o.id} className={`clickable${o.id === selectedId ? ' is-selected' : ''}${o.status === 'pending' ? ' is-pending' : ''}`} onClick={() => setSelectedId(o.id)}>
                      <td className="tnum">{toDate(o)?.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) || '—'}</td>
                      <td className="tnum ko-no">{shortId(o.id)}</td>
                      <td>
                        <div className="ko-cust-cell">{o.userName || '—'}{userOrderCounts[o.userId] <= 1 && <span className="k-badge">Yeni müşteri</span>}</div>
                      </td>
                      <td>{o.orderType === 'pickup' ? 'Gel-al' : 'Teslimat'}{scheduledLabel(o) && <span className="k-badge amber" style={{ marginLeft: 6 }}>İleri saat · {scheduledLabel(o)}</span>}</td>
                      <td>{o.paymentMethod || '—'}</td>
                      <td className="tnum" style={{ textAlign: 'right', fontWeight: 700 }}>{tl(o.totalPrice)}</td>
                      <td><StatusBadge status={o.status} /></td>
                      <td className={`tnum${late ? ' ko-late' : ''}`} style={{ textAlign: 'right' }}>{ACTIVE.includes(o.status) ? `${mins} dk` : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <aside className="k-card ko-detail">
          {selected ? (
            <OrderDetail
              order={selected} now={now} isNewCustomer={userOrderCounts[selected.userId] <= 1}
              countdown={countdown(selected)} action={nextAction(selected)} busy={busyId === selected.id}
              onAdvance={(a) => updateStatus(selected, a.status)}
              onCancel={() => setCancelTarget(selected)}
              onNotify={() => setNotifyTarget(selected)}
              onPrint={() => printNewOrderReceipt(selected, userOrderCounts)}
              onPrintCancel={() => printCancelReceipt(selected)}
            />
          ) : (
            <Empty icon="orders" title="Sipariş seçin">Ayrıntıları görmek için listeden bir siparişe tıklayın.</Empty>
          )}
        </aside>
      </div>

      {cancelTarget && (
        <Modal
          title={cancelTarget.status === 'pending' ? 'Siparişi reddet' : 'Siparişi iptal et'}
          width={440}
          onClose={() => setCancelTarget(null)}
          footer={(
            <>
              <button type="button" className="k-btn k-btn-secondary" onClick={() => setCancelTarget(null)}>Vazgeç</button>
              <button type="button" className="k-btn k-btn-danger-solid" onClick={async () => { await updateStatus(cancelTarget, 'cancelled'); setCancelTarget(null); }}>
                {cancelTarget.status === 'pending' ? 'Reddet' : 'İptal et'}
              </button>
            </>
          )}
        >
          <p style={{ margin: 0 }}><strong>{shortId(cancelTarget.id)}</strong> · {cancelTarget.userName || 'Müşteri'} · {tl(cancelTarget.totalPrice)}</p>
          <p style={{ margin: '8px 0 0' }}>Müşteriye siparişin iptal edildiği bildirilir. Bu işlem geri alınamaz.</p>
        </Modal>
      )}

      {notifyTarget && <NotifyModal order={notifyTarget} onClose={() => setNotifyTarget(null)} />}
    </div>
  );
}

// ── Sipariş detayı (sağ panel) ──────────────────────────────────────
export function OrderDetail({ order: o, now, isNewCustomer, countdown, action, busy, onAdvance, onCancel, onNotify, onPrint, onPrintCancel }) {
  const phone = o.addressPhone || o.userPhone;
  const verifying = o.status === 'pending' && o.serverVerified !== true;
  const steps = o.orderType === 'pickup' ? ['pending', 'preparing', 'ready', 'delivered'] : ['pending', 'preparing', 'on_the_way', 'delivered'];
  const stepIdx = steps.indexOf(o.status);

  return (
    <div className="ko-d">
      <div className="ko-d-head">
        <div>
          <div className="ko-d-id tnum">{shortId(o.id)}</div>
          <div className="ko-d-time">{fmtDateTime(toDate(o))} · {minutesSince(o, now)} dk önce</div>
        </div>
        <StatusBadge status={o.status} />
      </div>

      {o.status !== 'cancelled' && (
        <ol className="ko-d-steps">
          {steps.map((s, i) => <li key={s} className={i < stepIdx ? 'done' : i === stepIdx ? 'current' : ''}>{ORDER_STATUS[s].label}</li>)}
        </ol>
      )}
      {o.status === 'cancelled' && o.cancelReason && <div className="k-alert error">{o.cancelReason}</div>}
      {countdown !== null && !verifying && <div className="k-alert warn">{countdown} sn içinde otomatik kabul edilecek.</div>}
      {verifying && <div className="k-alert warn">Sipariş sunucuda doğrulanıyor…</div>}

      <dl className="ko-d-info">
        <div><dt>Müşteri</dt><dd>{o.userName || '—'}{isNewCustomer && <span className="k-badge" style={{ marginLeft: 6 }}>Yeni müşteri</span>}</dd></div>
        {phone && <div><dt>Telefon</dt><dd><a href={`tel:${phone}`}>{phone}</a>{o.userPhone && o.addressPhone && o.userPhone !== o.addressPhone ? ` · hesap: ${o.userPhone}` : ''}</dd></div>}
        <div><dt>{o.orderType === 'pickup' ? 'Teslim' : 'Adres'}</dt><dd>{o.orderType === 'pickup' ? 'Gel-al (restorandan alacak)' : (o.address || '—')}</dd></div>
        {scheduledLabel(o) && <div><dt>İleri saat</dt><dd><strong>{scheduledLabel(o)}</strong> için hazırlanacak</dd></div>}
        {o.zoneName && <div><dt>Bölge</dt><dd>{o.zoneName}</dd></div>}
        <div><dt>Ödeme</dt><dd>{o.paymentMethod || '—'} (kapıda)</dd></div>
      </dl>

      {(o.note || o.courierNote) && (
        <div className="ko-d-notes">
          {o.note && <div><b>Restorana not:</b> {o.note}</div>}
          {o.courierNote && <div><b>Kuryeye not:</b> {o.courierNote}</div>}
        </div>
      )}

      <table className="ko-d-items">
        <tbody>
          {(o.items || []).map((it, i) => (
            <tr key={i}>
              <td className="tnum ko-d-qty">{it.quantity}×</td>
              <td>
                {it.name}
                {it.selectedOptions?.length > 0 && <div className="ko-d-sub">{it.selectedOptions.map((x) => `${x.title ? `${x.title}: ` : ''}${x.selectedItem}`).join(' · ')}</div>}
                {it.note && <div className="ko-d-sub"><b>Not:</b> {it.note}</div>}
              </td>
              <td className="tnum" style={{ textAlign: 'right' }}>{tl(it.price * it.quantity)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          {o.discount > 0 && <tr><td /><td>İndirim{o.appliedCouponCode ? ` (${o.appliedCouponCode})` : ''}</td><td className="tnum" style={{ textAlign: 'right' }}>−{tl(o.discount)}</td></tr>}
          {o.deliveryFee > 0 && <tr><td /><td>Teslimat ücreti</td><td className="tnum" style={{ textAlign: 'right' }}>{tl(o.deliveryFee)}</td></tr>}
          <tr className="ko-d-total"><td /><td>Toplam</td><td className="tnum" style={{ textAlign: 'right' }}>{tl(o.totalPrice)}</td></tr>
        </tfoot>
      </table>

      <div className="ko-d-actions">
        {o.status !== 'cancelled' && action && (
          <button type="button" className="k-btn k-btn-primary k-btn-lg k-btn-block" disabled={busy || verifying} onClick={() => onAdvance(action)}>
            <Icon name={action.icon} size={16} />{action.label}
          </button>
        )}
        <div className="ko-d-tools">
          {o.status !== 'pending' && o.status !== 'cancelled' && <button type="button" className="k-btn k-btn-secondary" onClick={onPrint}><Icon name="printer" size={15} />Fiş yazdır</button>}
          {o.status === 'cancelled' && <button type="button" className="k-btn k-btn-secondary" onClick={onPrintCancel}><Icon name="printer" size={15} />İptal fişi</button>}
          {phone && <a className="k-btn k-btn-secondary" href={`tel:${phone}`}><Icon name="phone" size={15} />Ara</a>}
          <button type="button" className="k-btn k-btn-secondary" onClick={onNotify}><Icon name="bell" size={15} />Bildirim</button>
          {!['cancelled', 'delivered'].includes(o.status) && (
            <button type="button" className="k-btn k-btn-danger" onClick={onCancel}>{o.status === 'pending' ? 'Reddet' : 'İptal et'}</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Müşteriye bildirim ──────────────────────────────────────────────
export function NotifyModal({ order, onClose }) {
  const toast = useToast();
  const [form, setForm] = useState({ title: '', body: '' });
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast('Başlık ve mesaj zorunlu.', 'error'); return; }
    setBusy(true);
    try {
      await updateDoc(doc(db, 'orders', order.id), {
        pendingNotification: { title: form.title.trim(), body: form.body.trim(), sentAt: new Date() },
      });
      toast('Bildirim gönderildi.', 'ok');
      onClose();
    } catch (e) {
      toast(`Gönderilemedi: ${e.message}`, 'error');
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Müşteriye bildirim gönder"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="k-btn k-btn-secondary" onClick={onClose}>Vazgeç</button>
          <button type="button" className="k-btn k-btn-primary" disabled={busy} onClick={send}>{busy ? 'Gönderiliyor…' : 'Gönder'}</button>
        </>
      )}
    >
      <p className="ko-muted" style={{ marginTop: 0 }}>{order.userName || 'Müşteri'} · {shortId(order.id)}</p>
      <div className="ko-templates">
        {NOTIFY_TEMPLATES.map((t) => (
          <button key={t.title} type="button" className="k-btn k-btn-secondary k-btn-sm" onClick={() => setForm({ title: t.title, body: t.body })}>{t.title}</button>
        ))}
      </div>
      <label className="k-field"><span>Başlık</span><input className="k-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></label>
      <label className="k-field"><span>Mesaj</span><textarea className="k-textarea" value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} /></label>
    </Modal>
  );
}

export default Orders;
